import React from 'react';
import { DollarSign } from 'lucide-react';

interface PriceRangeWidgetProps {
  minPrice: number;
  maxPrice: number;
  confidence?: 'low' | 'medium' | 'high';
  isRefining?: boolean;
}

const PriceRangeWidget: React.FC<PriceRangeWidgetProps> = ({
  minPrice,
  maxPrice,
  confidence = 'low',
  isRefining = false,
}) => {
  const confidenceColors = {
    low: 'bg-amber-100',
    medium: 'bg-emerald-100',
    high: 'bg-green-100',
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className={`rounded-lg p-6 ${confidenceColors[confidence]} transition-all duration-500`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Estimated Price Range</h3>
        {isRefining && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" />
            <span className="text-sm text-gray-600">Refining estimate...</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-3 mb-4">
        <div className="flex items-start">
          <DollarSign className="w-5 h-5 mt-1" />
          <span className="text-3xl font-bold">{formatPrice(minPrice).slice(1)}</span>
        </div>
        <span className="text-3xl font-bold text-gray-400">-</span>
        <div className="flex items-start">
          <DollarSign className="w-5 h-5 mt-1" />
          <span className="text-3xl font-bold">{formatPrice(maxPrice).slice(1)}</span>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>Confidence Level:</span>
        <div className="flex gap-1">
          {['low', 'medium', 'high'].map((level) => (
            <div
              key={level}
              className={`w-8 h-1 rounded-full transition-colors ${
                confidence === level
                  ? 'bg-black'
                  : confidence === 'medium' && level === 'low'
                  ? 'bg-black'
                  : confidence === 'high' && (level === 'low' || level === 'medium')
                  ? 'bg-black'
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PriceRangeWidget;